/**
 * Cache de leitura para dados que quase não mudam e o front buscava de novo
 * a cada ticket aberto (New Relic 04-09: /tags/list 56 rpm, /companySettingOne
 * 39 rpm, /companies/listPlan 26 rpm — juntos ~20% do tempo de transação).
 *
 * Regras:
 *  - chamadas em paralelo com a mesma chave compartilham UMA requisição;
 *  - falha nunca vira cache (mesma lição do pluginsCache em ChannelIcon);
 *  - TTL curto: quem altera em outra aba/usuário é visto em até TTL;
 *  - quem escreve (update/save) invalida pelo prefixo.
 *
 * As chamadas a `registrar` alimentam o devtools (services/devtools.js) e
 * custam uma comparação booleana quando ele está desligado — ver a guarda
 * na primeira linha de `registrar`.
 */
import { registrar } from "./devtools";

const entries = new Map(); // key -> { value, expiresAt }
const inflight = new Map(); // key -> Promise

// Teto de chaves guardadas. Existe porque nem toda chave vence a tempo: a
// rolagem infinita de contatos cria uma por página e cada busca digitada cria
// outra, então em poucos minutos dá para acumular centenas ainda válidas.
const MAX_ENTRADAS = 200;

/**
 * Interruptor vindo do backend (GET /settings/frontend-cache).
 *
 * Começa LIGADO: se a consulta falhar ou demorar, o app se comporta como
 * antes desta configuração existir — desligar é a exceção, e depender de uma
 * requisição bem-sucedida para ligar deixaria o cache fora do ar sempre que
 * essa rota tivesse problema.
 */
let ligado = true;

const now = () => Date.now();

/**
 * Serialização ESTÁVEL dos parâmetros — a chave não pode depender da ORDEM em
 * que quem chama montou o objeto.
 *
 * `JSON.stringify` preserva a ordem de inserção, então `{searchParam, page}` e
 * `{page, searchParam}` viram strings diferentes: a mesma leitura ocuparia
 * duas entradas e a segunda pagaria rede achando que não tinha cache. Falha
 * silenciosa — nada quebra, só fica mais lento sem motivo aparente.
 *
 * Ausente, `null` e `{}` também são a mesma leitura.
 */
export const chaveDeParams = valor => {
  if (valor === undefined || valor === null) return "{}";
  if (Array.isArray(valor)) return `[${valor.map(chaveDeParams).join(",")}]`;
  if (typeof valor !== "object") return JSON.stringify(valor);
  const pares = Object.keys(valor)
    .sort()
    .filter(k => valor[k] !== undefined)
    .map(k => `${JSON.stringify(k)}:${chaveDeParams(valor[k])}`);
  return `{${pares.join(",")}}`;
};

/**
 * Tira o que venceu e, se ainda passar do teto, o que vence primeiro.
 *
 * Sem isto o Map só crescia: uma entrada vencida que ninguém lê de novo nunca
 * era removida — `cached` só sobrescreve na releitura da MESMA chave.
 */
const podar = () => {
  const agora = now();
  entries.forEach((e, chave) => {
    if (e.expiresAt <= agora) entries.delete(chave);
  });

  if (entries.size <= MAX_ENTRADAS) return;
  // Descarta primeiro quem está mais perto de vencer: é o que menos rende se
  // ficar.
  [...entries.entries()]
    .sort((a, b) => a[1].expiresAt - b[1].expiresAt)
    .slice(0, entries.size - MAX_ENTRADAS)
    .forEach(([chave]) => entries.delete(chave));
};

/**
 * `ttlMs` pode ser número ou função do valor recebido: quando só a resposta
 * sabe quanto vale guardar (ex.: auditoria devolve `immutable`).
 */
const cached = async (key, fetcher, ttlMs) => {
  // Desligado, vira uma passagem direta: nem lê nem guarda. O `miss` continua
  // sendo registrado no devtools, senão a barra pareceria morta e não daria
  // para distinguir "sem tráfego" de "cache desligado".
  if (!ligado) {
    registrar("cache", "miss", key, () => ({ desligado: true }));
    return fetcher();
  }

  const hit = entries.get(key);
  if (hit && hit.expiresAt > now()) {
    registrar("cache", "hit", key, () => ({ expiraEm: hit.expiresAt - now() }));
    return hit.value;
  }

  const pending = inflight.get(key);
  if (pending) {
    registrar("cache", "compartilhada", key);
    return pending;
  }

  registrar("cache", "miss", key, () => ({ ttlMs }));
  const p = (async () => {
    try {
      const value = await fetcher();
      const ttl = typeof ttlMs === "function" ? ttlMs(value) : ttlMs;
      entries.set(key, { value, expiresAt: now() + ttl });
      podar();
      return value;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
};

const invalidate = (prefix, motivo) => {
  const derrubadas = [];
  for (const key of entries.keys()) {
    if (key.startsWith(prefix)) {
      derrubadas.push(key);
      entries.delete(key);
    }
  }
  registrar("cache", "invalidacao", prefix, () => ({ motivo, derrubadas }));
};

const clear = () => {
  entries.clear();
  inflight.clear();
};

/**
 * Liga/desliga por ordem do backend. Desligar joga fora o que já estava
 * guardado — manter seria continuar servindo dado de um mecanismo que alguém
 * acabou de dizer para não usar.
 */
const definirLigado = valor => {
  ligado = !!valor;
  if (!ligado) clear();
  registrar("cache", "invalidacao", "*", () => ({
    motivo: ligado ? "backend:cache ligado" : "backend:cache desligado"
  }));
};

const estaLigado = () => ligado;

/**
 * O que está em cache AGORA, com quanto falta para expirar.
 *
 * Só o devtools consome: é a diferença entre ver "houve um hit às 09:41" e ver
 * "esta chave ainda serve por 42s". A segunda é a que responde por que uma
 * tela não atualizou — o dado ainda estava dentro do TTL.
 */
const estado = () => {
  const agora = now();
  return [...entries.entries()]
    .map(([chave, e]) => ({
      chave,
      expiraEm: e.expiresAt,
      restanteMs: e.expiresAt - agora,
      vencido: e.expiresAt <= agora,
      // O valor guardado. Só o devtools lê — é o que responde "o cache está
      // servindo O QUÊ?", que é diferente de "o cache pegou?".
      valor: e.value
    }))
    .sort((a, b) => a.restanteMs - b.restanteMs);
};

export const requestCache = {
  cached,
  chaveDeParams,
  definirLigado,
  estaLigado,
  invalidate,
  clear,
  estado,
  PLAN_TTL_MS: 5 * 60 * 1000,
  SETTINGS_TTL_MS: 60 * 1000,
  TAGS_TTL_MS: 60 * 1000
};
